/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Small shared pieces of the athlete feed: the Material Symbols icon, the
 * AthloHive mark, and the loading / empty / error states every list uses.
 */

import React from 'react';
import Icon from './FeedIcon';
import PlayStoreIcon, { PLAY_STORE_URL } from '../PlayStoreIcon';
import AppStoreBadge from '../AppStoreBadge';
import { EVENTS_PATH } from '../../pages/feed/feedRoutes';

export { default as Icon } from './FeedIcon';

export function BrandMark({ className = 'fd-brand-mark' }) {
  return (
    <svg className={className} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 2.5l11.7 6.75v13.5L16 29.5 4.3 22.75V9.25z" fill="var(--fd-accent)" />
      <path d="M10 19.5l4-7 3 4.5 2-3 3 5.5" fill="none" stroke="#fff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Skeleton({ height, radius = 16, style }) {
  return <div className="fd-skel" style={{ height, borderRadius: radius, ...style }} aria-hidden="true" />;
}

export function FeedSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 16, marginTop: 24 }} aria-busy="true" aria-label="Loading your activities">
      <Skeleton height={440} radius={24} />
      <Skeleton height={440} radius={24} />
    </div>
  );
}

/** First visit, nothing synced yet. */
export function Onboarding() {
  return (
    <div className="fd-onboard">
      <div>
        <span className="fd-eyebrow">No activities yet</span>
        {/* <h3>Record in UpVibe. It lands here automatically.</h3> */}
        <h3>Record in AthloHive. It lands here automatically.</h3>
        {/* <p>
          Runs, rides, swims, hikes and yoga you record in the UpVibe app sync to your
          account, with maps, splits and your effort.
        </p> */}
        <p>
          Runs, rides, swims, hikes and yoga you record in the AthloHive app sync to your
          account, with maps, splits and your effort.
        </p>
      </div>
      <ol className="fd-steps">
        {/* <li><b>Get UpVibe</b>Free on Android</li> */}
        {/* <li><b>Get AthloHive</b>Free on Android</li> */}
        <li><b>Get AthloHive</b>Available free on the App Store and Google Play</li>
        <li><b>Sign in</b>Use the same email you use here</li>
        <li><b>Tap Start</b>On your next workout</li>
      </ol>
      <div className="fd-ob-btns">
        {/* Step 1 is the actual next step for someone with no activities, so it
            leads here; finding an event steps back to the outline style.
            Apple's guidelines put the App Store badge first in a lineup. */}
        <AppStoreBadge />
        <a
          className="fd-btn fd-btn-primary"
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <PlayStoreIcon />Get it on Google Play
        </a>
        {/* <a className="fd-btn fd-btn-primary" href={EVENTS_PATH}>
          <Icon name="calendar_month" />Find an event to train for
        </a> */}
        <a className="fd-btn fd-btn-line" href={EVENTS_PATH}>
          <Icon name="calendar_month" />Find an event to train for
        </a>
      </div>
    </div>
  );
}

/**
 * Right-rail card with both store links. Always shown, unlike the empty-state
 * buttons above, so the app stays one click away once activities exist.
 */
export function GetTheApp() {
  return (
    <section className="fd-rc" aria-label="Get the AthloHive app">
      <h3>Get the app</h3>
      <p className="fd-empty-rc">Record runs, rides and more. They sync here automatically.</p>
      <div className="fd-store-btns">
        <AppStoreBadge />
        <a className="fd-btn fd-btn-line" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
          <PlayStoreIcon />Google Play
        </a>
      </div>
    </section>
  );
}

export function EmptyFilter({ query, onClear }) {
  return (
    <div className="fd-empty">
      <h3>No activities match</h3>
      <p>{query ? `Nothing called “${query}”.` : 'Nothing of this type yet.'} Try another filter.</p>
      <button type="button" className="fd-btn fd-btn-line" onClick={onClear}>Show all activities</button>
    </div>
  );
}

export function LoadError({ what = 'your activities', onRetry }) {
  return (
    <div className="fd-empty" role="alert">
      <h3>We couldn’t load {what}</h3>
      <p>Check your connection and try again.</p>
      {onRetry && <button type="button" className="fd-btn fd-btn-line" onClick={onRetry}>Try again</button>}
    </div>
  );
}
